import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveLegacyTaskType } from 'core/slices/utils/constants';
import {
  Autocomplete,
  Box,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CloseIcon from '@mui/icons-material/Close';
import BaseModal from '../templates/BaseModal';
import ExcelDropzone from '../templates/ExcelDropzone';

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2.5,
    pt: 2.5,
    pb: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerText: { flexGrow: 1 },
  closeButton: { color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'transparent' } },
  dialogContent: { px: 2.5, py: 3 },
  autocomplete: { mb: 3 },
  headerIconColor: { fontSize: 20, color: 'text.secondary' },
};

export default function ExcelUploadModal({ open, onClose, reportOptions, onUpload }) {
  const { t } = useTranslation();
  const [selectedReport, setSelectedReport] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleClose = () => {
    if (uploading) return;
    setSelectedReport(null);
    onClose();
  };

  const handleUpload = async (file) => {
    if (!selectedReport) return false;
    setUploading(true);
    try {
      const result = await onUpload({ task_type: selectedReport.task_type, file, searchExcel: true });
      if (result?.success) handleClose();
      return result?.success ?? false;
    } finally {
      setUploading(false);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      dialogMaxWidth="xs"
      dialogWidth={400}
      dialogHeight="auto"
      drawerHeight="auto"
      bgcolor="background.paper"
    >
      {/* ── Header ── */}
      <Box sx={styles.header}>
        <Box sx={styles.headerIcon}>
          <ImportExportIcon sx={styles.headerIconColor} />
        </Box>
        <Box sx={styles.headerText}>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3} sx={{ color: 'text.primary' }}>
            {t('bruno.excel.import_title', 'Import')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('bruno.excel.import_description', 'Upload an Excel file to create task data or close tasks.')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={styles.closeButton} disabled={uploading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={styles.dialogContent}>
        {/* ── Report type selector ── */}
        <Autocomplete
          options={(reportOptions || []).filter(o => o.excel_reporting === true)}
          getOptionLabel={(option) => t(`bruno.tasks_names.${resolveLegacyTaskType(option.task_type)}`, option.task_type)}
          value={selectedReport}
          onChange={(_, value) => setSelectedReport(value)}
          sx={styles.autocomplete}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('bruno.buttons.report', 'Report')}
              size="small"
              fullWidth
              InputProps={{
                ...params.InputProps,
                autoComplete: 'off',
                readOnly: true,
              }}
            />
          )}
        />

        {/* ── Dropzone ── */}
        <ExcelDropzone
          uploading={uploading}
          disabled={!selectedReport}
          onUpload={handleUpload}
          warning={t(
            'bruno.excel.column_warning',
            'The first column must correspond to Item and the second to the product Barcode.',
          )}
        />
      </DialogContent>
    </BaseModal>
  );
}
